"use client";

import { useState, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  Stack,
  Fade,
  Zoom,
  TextField,
} from "@mui/material";
import {
  CloudUpload,
  Delete,
  DataObject,
  Close,
  CheckCircle,
  Code,
  ContentCopy,
  Download,
} from "@mui/icons-material";

interface XmlFile {
  file: File;
  id: string;
}

export default function XmlToJsonConverter() {
  const [xmlFile, setXmlFile] = useState<XmlFile | null>(null);
  const [xmlText, setXmlText] = useState<string>("");
  const [jsonResult, setJsonResult] = useState<string>("");
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const fileName = file.name.toLowerCase();
    const isValidExtension = fileName.endsWith(".xml");
    const validTypes = ["text/xml", "application/xml"];

    if (validTypes.includes(file.type) || isValidExtension) {
      setXmlFile({
        file,
        id: Math.random().toString(36).substr(2, 9),
      });
      setError(null);
      setSuccess(false);
      setJsonResult("");
      setXmlText("");

      // Read file content for preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setXmlText((e.target?.result as string) || "");
      };
      reader.readAsText(file);
    } else {
      setError(`${file.name} is not a supported format. Please use XML files.`);
    }
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [handleFileSelect]
  );

  const removeFile = () => {
    setXmlFile(null);
    setXmlText("");
    setJsonResult("");
    setError(null);
    setSuccess(false);
    const fileInput = document.getElementById(
      "xml-file-input"
    ) as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const convertToJson = async () => {
    const xmlContent = xmlFile ? xmlText : xmlText;

    if (!xmlContent.trim()) {
      setError("Please provide XML content to convert.");
      return;
    }

    setConverting(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await axios.post(
        "/api/xml-to-json",
        { xml: xmlContent },
        {
          timeout: 600000,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      setJsonResult(JSON.stringify(response.data.json, null, 2));
      setSuccess(true);
    } catch (err) {
      let errorMessage = "Failed to convert XML to JSON";
      if (axios.isAxiosError(err) && err.response) {
        const errorData = err.response.data;
        errorMessage = errorData.error || errorMessage;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setConverting(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(jsonResult);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Failed to copy to clipboard");
    }
  };

  const downloadJson = () => {
    const blob = new Blob([jsonResult], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const fileName = xmlFile
      ? xmlFile.file.name.replace(/\.xml$/i, ".json")
      : "converted.json";
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Upload Area */}
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            border: "3px dashed",
            borderColor: dragActive ? "primary.main" : "grey.300",
            borderRadius: 3,
            p: 4,
            textAlign: "center",
            bgcolor: dragActive ? "primary.50" : "grey.50",
            cursor: "pointer",
            transition: "all 0.3s ease",
            position: "relative",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "primary.50",
            },
          }}
          onClick={() => document.getElementById("xml-file-input")?.click()}
        >
          <input
            id="xml-file-input"
            type="file"
            accept=".xml,text/xml,application/xml"
            onChange={(e) => handleFileSelect(e.target.files)}
            style={{ display: "none" }}
          />

          <CloudUpload
            sx={{
              fontSize: 80,
              color: dragActive ? "primary.main" : "grey.400",
              mb: 2,
            }}
          />

          <Typography
            variant="h6"
            component="p"
            gutterBottom
            fontWeight={600}
            color="text.primary"
          >
            {dragActive
              ? "Drop your XML file here"
              : "Click to upload or drag and drop"}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Supported format: XML
          </Typography>

          <Button
            variant="contained"
            startIcon={<Code sx={{ color: "#ffffff" }} />}
            sx={{
              mt: 3,
              color: "#ffffff",
              backgroundColor: "#5558d3",
              "&:hover": {
                backgroundColor: "#4448c2",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              document.getElementById("xml-file-input")?.click();
            }}
          >
            Select XML File
          </Button>
        </Box>

        {/* Selected File Preview */}
        {xmlFile && (
          <Fade in={!!xmlFile}>
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="p" fontWeight={600}>
                  Selected File
                </Typography>
                <Button
                  size="small"
                  startIcon={<Delete />}
                  onClick={removeFile}
                  color="error"
                  variant="outlined"
                >
                  Remove
                </Button>
              </Box>

              <Zoom in>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    bgcolor: "grey.50",
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      bgcolor: "#E65100",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Code sx={{ fontSize: 32, color: "white" }} />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {xmlFile.file.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(xmlFile.file.size)}
                    </Typography>
                  </Box>

                  <IconButton
                    onClick={removeFile}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.9)",
                      "&:hover": {
                        bgcolor: "error.main",
                        color: "white",
                      },
                    }}
                  >
                    <Close />
                  </IconButton>
                </Paper>
              </Zoom>
            </Box>
          </Fade>
        )}

        {/* Or paste XML text */}
        {!xmlFile && (
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="h6"
              component="p"
              fontWeight={600}
              sx={{ mb: 2 }}
            >
              Or paste your XML content
            </Typography>
            <TextField
              multiline
              rows={8}
              fullWidth
              placeholder="Paste your XML content here..."
              value={xmlText}
              onChange={(e) => setXmlText(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                },
              }}
            />
          </Box>
        )}

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mt: 3 }}
            icon={<CheckCircle />}
            onClose={() => setSuccess(false)}
          >
            XML converted to JSON successfully!
          </Alert>
        )}

        {/* Convert Button */}
        <Box sx={{ mt: 4 }}>
          {converting && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<DataObject sx={{ color: "#ffffff" }} />}
            onClick={convertToJson}
            disabled={converting || (!xmlFile && !xmlText.trim())}
            style={{
              color: "#ffffff",
            }}
            sx={{
              py: 2,
              fontSize: "1.1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {converting ? "Converting..." : "Convert to JSON"}
          </Button>

          {(xmlFile || xmlText.trim()) && (
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              sx={{ mt: 2 }}
            >
              <Chip
                icon={<Code />}
                label="XML Document"
                color="primary"
                variant="outlined"
              />
              {xmlFile && (
                <Chip
                  label={formatFileSize(xmlFile.file.size)}
                  variant="outlined"
                />
              )}
            </Stack>
          )}
        </Box>

        {/* JSON Result */}
        {jsonResult && (
          <Fade in={!!jsonResult}>
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="p" fontWeight={600}>
                  JSON Result
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={<ContentCopy />}
                    onClick={copyToClipboard}
                    variant="outlined"
                  >
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Download />}
                    onClick={downloadJson}
                    variant="contained"
                    sx={{
                      background:
                        "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                    }}
                  >
                    Download
                  </Button>
                </Stack>
              </Box>

              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  bgcolor: "#1e1e1e",
                  borderRadius: 2,
                  maxHeight: 400,
                  overflow: "auto",
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    color: "#d4d4d4",
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {jsonResult}
                </pre>
              </Paper>
            </Box>
          </Fade>
        )}
      </Paper>
    </Box>
  );
}
